/**
 * Quote stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const quoteStickersPack: ToilePack = {
    slug: 'quote-stickers',
    category: 'sticker',
    name: 'Quote stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'bubble', label: 'Speech bubble', subject: 'a speech bubble lettered "MAKE IT HAPPEN"'},
        {key: 'kind', label: 'Be kind', subject: 'the words "BE KIND" lettered in a rounded script with a heart'},
        {key: 'curious', label: 'Stay curious', subject: 'the words "STAY CURIOUS" lettered in bold with a small star'},
    ],
    styles: [
        {key: 'retro', label: 'Retro', style: 'retro sticker illustration, 1970s shapes, halftone grain, slight misregistration', palette: 'Mustard, rust, teal and cream', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'bold', label: 'Bold cartoon', style: 'bold cartoon sticker, thick black outlines, bright flat fills, slight gloss', palette: 'Bright saturated primaries with black outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'minimal', label: 'Minimal line', style: 'minimal single-line sticker, one clean continuous line, no fill', palette: 'Black line only, one accent colour', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['a be brave line instead', 'a whole sticker sheet', 'in soft pastel colors'],
    directionHint: 'try: a be brave line instead',
    seo: {
        title: 'Quote Stickers: Speech Bubble, Be Kind, Stay Curious',
        metaDescription: 'Nine lettered quote stickers — a speech-bubble quote, be kind, stay curious — in retro, bold, and minimal styles.',
        h1: 'Quote stickers: speech bubble, be kind, stay curious in three styles',
        tagline: 'Nine lettered quote stickers, three sayings, three styles each.',
        intro: "The lettered sticker for a page or a lid: a short quote in a speech bubble, a lettered be kind, a lettered stay curious. Rendered retro, bold, and minimal so the phrase matches whatever it's going on — a journal, a laptop, a classroom door. Ask for your own quote, and the same three styles carry it.",
        useCases: [
            'a journal or scrapbook page quote',
            'a classroom or office door sticker',
            'a laptop or notebook cover sticker',
            'a card or gift tag saying',
            'a printable quote sticker sheet',
        ],
        faq: [
            {q: 'Can I get a specific quote instead of these three?', a: 'Yes — the direction field takes any short saying, lettered in the same three styles.'},
            {q: "What's the difference between retro, bold, and minimal?", a: 'Retro uses a vintage script and warm palette, bold is a thick high-contrast sans-serif, and minimal is a clean single-weight line — pick whichever fits your page.'},
            {q: 'How is this different from the motivational sticker pack?', a: 'This pack leans on speech-bubble and word-lettering phrasing (be kind, stay curious); the motivational pack is shorter direct encouragement — pick whichever tone fits.'},
            {q: 'Is there a word-count limit on the quote?', a: 'Very long sentences will crowd the bordered layout — short phrases and single sentences render most cleanly.'},
            {q: 'Can I use these in a classroom bulletin board?', a: 'Yes, for personal and classroom use; check the license terms before reselling the designs.'},
        ],
    },
}
